// MACHINERY V2 (Stroje) — dialog host for the PROFILE surface (M2.4).
//
// >>> THE SECOND (AND ONLY OTHER) SANCTIONED MUI IMPORT SITE IN v2. <<<
//
// Ticket §5: the kebab dialogs (QR / Vyradiť / Upraviť / skupiny) are listed by
// the designer but NOT redesigned — v2 reuses the existing MUI dialogs, which
// open over the v2 surface, until each is separately scheduled. They therefore
// have to be mounted somewhere, and fleet/FleetDialogs.tsx is the pattern:
// confine every MUI import to ONE file per surface so the v2 stack rule ("no
// MUI anywhere a v2 surface is drawn") still holds everywhere else, and so the
// imports vanish from exactly one place when the dialogs are redesigned.
//
// WHY NOT REUSE fleet/FleetDialogs.tsx DIRECTLY: its refresh callbacks are
// fleet-shaped (`getMachines(query)` / `getDecommissionedMachines()`), and it
// mounts the two dialogs only the fleet has (add-machine, manage-groups) while
// missing the one only the profile has (Report Problem). A profile mutation
// must refetch THIS machine — `getMachine(id)` — or the hero keeps rendering
// pre-edit values and the decommission banner never appears. Same pattern, same
// underlying components, surface-correct wiring; FleetDialogs stays untouched.
//
// Nothing here is styled, wrapped or re-skinned. This is a mount point, not a
// component. Dialog STATE lives in useProfileActions.ts, next to the action set
// that opens it.
import React from "react";

import { Machine } from "../../models/models";
import AssignGroupDialog from "../../components/AssignGroupDialog";
import DecommissionDialog from "../../components/DecommissionDialog";
import MachineEditDialog from "../../components/MachineEditDialog";
import MachineQRCodeDialog from "../../components/MachineQRCodeDialog";
import ReportProblemDialog from "../../components/ReportProblemDialog";

export interface ProfileDialogsProps {
  machine: Machine;
  qrOpen: boolean;
  onQrClose: () => void;
  editOpen: boolean;
  onEditClose: () => void;
  onEditSaved: () => void;
  assignGroupOpen: boolean;
  onAssignGroupClose: () => void;
  onAssigned: () => void;
  decommissionOpen: boolean;
  onDecommissionClose: () => void;
  onDecommissioned: () => void;
  reportProblemOpen: boolean;
  onReportProblemClose: () => void;
  onReportProblemSuccess: (srNumber: string) => void;
}

const ProfileDialogs: React.FC<ProfileDialogsProps> = ({
  machine,
  qrOpen,
  onQrClose,
  editOpen,
  onEditClose,
  onEditSaved,
  assignGroupOpen,
  onAssignGroupClose,
  onAssigned,
  decommissionOpen,
  onDecommissionClose,
  onDecommissioned,
  reportProblemOpen,
  onReportProblemClose,
  onReportProblemSuccess,
}) => (
  <>
    {qrOpen && (
      <MachineQRCodeDialog open={qrOpen} machine={machine} onClose={onQrClose} />
    )}

    {editOpen && (
      <MachineEditDialog
        open
        machine={machine}
        onClose={onEditClose}
        onSaved={onEditSaved}
      />
    )}

    <AssignGroupDialog
      open={assignGroupOpen}
      machine={assignGroupOpen ? machine : null}
      onClose={onAssignGroupClose}
      onAssigned={onAssigned}
    />

    <DecommissionDialog
      open={decommissionOpen}
      machine={decommissionOpen ? machine : null}
      onClose={onDecommissionClose}
      onDecommissioned={onDecommissioned}
    />

    {reportProblemOpen && (
      <ReportProblemDialog
        open
        machine={machine}
        onClose={onReportProblemClose}
        onSuccess={onReportProblemSuccess}
      />
    )}
  </>
);

export default ProfileDialogs;
